import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { getReport } from '../../features/reports/report-definitions';
import { Division } from '../../features/reports/report.model';

/** A single breadcrumb hop. The last crumb is the current page (no link). */
interface Crumb {
  label: string;
  /** Optional PrimeIcon class (e.g. the home icon on the first crumb). */
  icon?: string;
  /** Router target; omitted for the current page. */
  link?: string | unknown[];
  current?: boolean;
}

/**
 * Global breadcrumb bar shown under the header on every screen.
 *
 * Derived from the current URL: Home ▸ Bordereau ▸ Division ▸ Report. Report
 * titles are resolved from the report definitions so the trail reads naturally.
 */
@Component({
  selector: 'app-breadcrumb',
  standalone: false,
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss'
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  crumbs: Crumb[] = [];
  private sub?: Subscription;

  constructor(private readonly router: Router) {}

  ngOnInit(): void {
    this.build(this.router.url);
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.build(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Rebuilds the trail from the given URL. */
  private build(url: string): void {
    const segments = url.split('?')[0].split('/').filter(Boolean);

    // Landing page ('/') has no trail — hide the bar entirely.
    if (segments.length === 0) {
      this.crumbs = [];
      return;
    }

    const crumbs: Crumb[] = [
      { label: '', icon: 'pi pi-home', link: '/' },
      { label: 'Bordereau', link: '/' }
    ];

    if (segments[0] === 'actuary' || segments[0] === 'finance') {
      const division = segments[0] as Division;
      const divisionLabel = division === 'actuary' ? 'Actuary' : 'Finance';
      if (segments.length > 1) {
        crumbs.push({ label: divisionLabel, link: '/' + division });
        const report = getReport(division, segments[1]);
        crumbs.push({ label: report?.title ?? segments[1] });
      } else {
        crumbs.push({ label: divisionLabel });
      }
    } else if (segments[0] === 'treasury') {
      crumbs.push({ label: 'Load Treasury rates' });
    } else if (segments[0] === 'treasury-rates') {
      crumbs.push({ label: 'US Treasury rates' });
    } else if (segments[0] === 'approvals') {
      crumbs.push({ label: 'Approval Dashboard' });
    }

    // The final crumb is the current page — render it as plain text, not a link.
    const last = crumbs[crumbs.length - 1];
    last.current = true;
    last.link = undefined;

    this.crumbs = crumbs;
  }
}
